import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useFlashMessage } from './FlashMessage';
import axiosInstance from '../utils/axiosConfig';
import LeaveRequestModal from './LeaveRequestModal';
import LeaveRequestViewModal from './LeaveRequestViewModal';
import EditLeaveRequestModal from './EditLeaveRequestModal';
import DayLeaveDetailsModal from './DayLeaveDetailsModal';
import CancelLeaveConfirmModal from './CancelLeaveConfirmModal';
import ClearLeaveConfirmModal from './ClearLeaveConfirmModal';
import LeaveCalendarWidget from './LeaveCalendarWidget';
import { LoadingSpinner } from './ui';
import './Settings.css';
import './UserLeaveManagement.css';
import withAuth from '../utils/withAuth';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import Layout from './Layout';
import { AuthContext } from '../context/AuthContext';
import { getAbsoluteMediaUrl } from '../utils/mediaUtils';

function UserLeaveManagement() {
  const history = useHistory();
  const location = useLocation();
  const { showError, showSuccess } = useFlashMessage();
  const { user } = useContext(AuthContext);
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Use refs to store flash message functions to prevent dependency issues
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  showErrorRef.current = showError;
  showSuccessRef.current = showSuccess;
  
  // Leave data state
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const selectedYearRef = useRef(new Date().getFullYear());
  const [hasMultiYearLeaves, setHasMultiYearLeaves] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Current user avatar state
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);
  
  // Supervisor/Team state
  const [supervisorInfo, setSupervisorInfo] = useState({
    is_supervisor: false,
    is_head_of_department: false,
    has_supervised_users: false,
    supervised_user_count: 0,
    has_team: false,
    departments: []
  });
  const [teamLeaveRequests, setTeamLeaveRequests] = useState([]);
  const [calendarView, setCalendarView] = useState('my'); // 'my' or 'team'
  const [selectedDepartment, setSelectedDepartment] = useState(null); // null = All
  
  // UI state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [requestToEdit, setRequestToEdit] = useState(null);
  const [showDayLeaveModal, setShowDayLeaveModal] = useState(false);
  const [selectedDayLeaveRequests, setSelectedDayLeaveRequests] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [prefilledStartDate, setPrefilledStartDate] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [leaveRequestsDisplayLimit, setLeaveRequestsDisplayLimit] = useState(10);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);
  const [isEarlyReturn, setIsEarlyReturn] = useState(false);
  const [daysToRefund, setDaysToRefund] = useState(0);
  const [showClearModal, setShowClearModal] = useState(false);
  const [requestToClear, setRequestToClear] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.actions-cell')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Manage dropdown z-index elevation and positioning
  useEffect(() => {
    // FIRST: Always remove dropdown-open from ALL dropdowns
    document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
      el.classList.remove('dropdown-open');
      const menu = el.querySelector('.dropdown-menu');
      if (menu) {
        menu.classList.remove('dropdown-menu-up');
      }
    });

    // THEN: If we have an active dropdown, add the class and adjust position
    if (activeDropdown && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const actionDropdown = dropdown.closest('.action-dropdown');
      
      if (actionDropdown) {
        // Add dropdown-open class for z-index elevation
        actionDropdown.classList.add('dropdown-open');
      }
      
      // Check if dropdown goes below viewport
      const rect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      if (rect.bottom > viewportHeight) {
        dropdown.classList.add('dropdown-menu-up');
      } else {
        dropdown.classList.remove('dropdown-menu-up');
      }
    }
  }, [activeDropdown]);

  // Lightweight function to fetch only leave balance for a specific year
  const fetchLeaveBalance = useCallback(async (year) => {
    try {
      const balanceResponse = await axiosInstance.get(`/api/leave/balance/?year=${year}`);
      setLeaveBalance(balanceResponse.data);
    } catch (err) {
      console.error('Error fetching leave balance:', err);
      showErrorRef.current('Failed to load leave balance');
    }
  }, []);

  const fetchLeaveData = useCallback(async (silent = false, yearToFetch = null) => {
    try {
      if (!silent) {
        setLoading(true);
        setError('');
      }

      // Use provided year or current selected year (from ref to avoid stale closure)
      const targetYear = yearToFetch !== null ? yearToFetch : selectedYearRef.current;

      // Fetch leave balance for specified year
      const balanceResponse = await axiosInstance.get(`/api/leave/balance/?year=${targetYear}`);
      setLeaveBalance(balanceResponse.data);

      // Fetch leave requests (always fetch all to show history)
      try {
        const requestsResponse = await axiosInstance.get('/api/leave/requests/');
        const requests = requestsResponse.data.results || requestsResponse.data;
        setLeaveRequests(requests);
        
        // Check if any ACTIVE leave requests span multiple years (exclude cancelled and rejected)
        const hasMultiYear = requests.some(request => {
          if (!request.start_date || !request.end_date) return false;
          // Only check active statuses (pending, approved, completed)
          if (request.status === 'cancelled' || request.status === 'rejected') return false;
          const startYear = new Date(request.start_date).getFullYear();
          const endYear = new Date(request.end_date).getFullYear();
          return startYear !== endYear;
        });
        setHasMultiYearLeaves(hasMultiYear);
        
        // Reset to current year if no multi-year leaves
        if (!hasMultiYear && selectedYearRef.current !== new Date().getFullYear()) {
          const currentYear = new Date().getFullYear();
          setSelectedYear(currentYear);
          selectedYearRef.current = currentYear;
        }
      } catch (requestsError) {
        console.warn('Failed to fetch leave requests:', requestsError);
        setLeaveRequests([]);
        setHasMultiYearLeaves(false);
      }

    } catch (err) {
      if (!silent) {
        setError('Error loading leave information');
        console.error('Error:', err);
        showErrorRef.current('Failed to load leave information');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []); // No dependencies - uses selectedYear from closure

  // Fetch supervisor info
  const fetchSupervisorInfo = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/leave/supervisor-info/');
      setSupervisorInfo(response.data);
      
      // If user has a team calendar, fetch team leave data
      if (response.data.has_team) {
        await fetchTeamLeaveData();
      }
    } catch (err) {
      console.warn('Failed to fetch supervisor info:', err);
      // Not a critical error, user just won't see team calendar toggle
    }
  }, []);

  // Fetch team leave calendar data, optionally filtered by department
  const fetchTeamLeaveData = useCallback(async (departmentId = null) => {
    try {
      const params = {};
      if (departmentId) params.department = departmentId;
      const response = await axiosInstance.get('/api/leave/team-calendar/', { params });
      setTeamLeaveRequests(response.data.results || response.data);
    } catch (err) {
      console.warn('Failed to fetch team leave data:', err);
      setTeamLeaveRequests([]);
    }
  }, []);

  // Handle calendar view change
  const handleCalendarViewChange = useCallback((view) => {
    setCalendarView(view);
    if (view === 'team' && supervisorInfo.has_team) {
      fetchTeamLeaveData(selectedDepartment);
    }
    if (view === 'my') {
      setSelectedDepartment(null);
    }
  }, [supervisorInfo.has_team, fetchTeamLeaveData, selectedDepartment]);

  // Handle department filter change
  const handleDepartmentChange = useCallback((departmentId) => {
    setSelectedDepartment(departmentId);
    fetchTeamLeaveData(departmentId);
  }, [fetchTeamLeaveData]);

  // Fetch leave data on component mount
  useEffect(() => {
    fetchLeaveData();
    fetchSupervisorInfo();
  }, [fetchLeaveData, fetchSupervisorInfo]); // Run on mount

  // Fetch current user profile data for avatar
  useEffect(() => {
    const fetchCurrentUserProfile = async () => {
      try {
        const response = await axiosInstance.get('/api/profile/me/');
        // API returns nested data structure: { success: true, data: { ... } }
        const profileData = response.data?.data || response.data;
        if (profileData?.avatar_photo) {
          const absoluteAvatarUrl = getAbsoluteMediaUrl(profileData.avatar_photo);
          console.log('✅ Current user avatar loaded:', absoluteAvatarUrl);
          setCurrentUserAvatar(absoluteAvatarUrl);
        } else {
          console.log('⚠️ No avatar photo found in profile data');
        }
      } catch (error) {
        console.error('❌ Error fetching current user profile:', error);
      }
    };
    
    if (user?.id) {
      fetchCurrentUserProfile();
    }
  }, [user]);

  const handleRequestLeave = () => {
    setShowRequestModal(true);
  };

  const handleRequestSubmitted = () => {
    // Refresh leave data after successful submission (silent to avoid reload)
    fetchLeaveData(true);
  };

  const handleCancelRequest = async () => {
    if (!requestToCancel) {
      return;
    }
    
    try {
      await axiosInstance.patch(`/api/leave/requests/${requestToCancel.id}/`, {
        status: 'cancelled'
      });
      
      // Refresh data silently to avoid triggering reload
      await fetchLeaveData(true);
      
      // Dispatch event to update notifications immediately
      window.dispatchEvent(new CustomEvent('notificationStatsChanged'));
      
      // Show success message
      const leaveTypeName = requestToCancel.leave_type_details?.name || 'Leave';
      showSuccessRef.current(`${leaveTypeName} request cancelled successfully`);
      
      setShowCancelModal(false);
      setRequestToCancel(null);
    } catch (error) {
      console.error('Error cancelling leave request:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.status?.[0] || 
                          'Failed to cancel leave request';
      showErrorRef.current(errorMessage);
      setShowCancelModal(false);
      setRequestToCancel(null);
    }
  };

  const handleShowCancelModal = (request) => {
    setRequestToCancel(request);
    setIsEarlyReturn(false);
    setDaysToRefund(0);
    setShowCancelModal(true);
  };

  const handleReturnEarly = (request) => {
    const refundDays = calculateDaysRefunded(request);
    setRequestToCancel(request);
    setIsEarlyReturn(true);
    setDaysToRefund(refundDays);
    setShowCancelModal(true);
  };

  const handleViewRequest = (requestId) => {
    setSelectedRequestId(requestId);
    setShowViewModal(true);
  };

  const handleEditLeave = (request) => {
    setRequestToEdit(request);
    setShowEditModal(true);
  };

  const handleRequestUpdated = () => {
    // Refresh leave data after successful edit (silent to avoid reload)
    fetchLeaveData(true);
  };

  const handleShowClearModal = (request) => {
    if (!request || request.status !== 'cancelled') {
      showErrorRef.current('Only cancelled leave requests can be cleared');
      return;
    }
    setRequestToClear(request);
    setShowClearModal(true);
    setActiveDropdown(null);
  };

  const handleClearRequest = async () => {
    if (!requestToClear) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/api/leave/requests/${requestToClear.id}/`);
      
      // Refresh data silently to avoid triggering reload
      await fetchLeaveData(true);
      
      // Show success message
      const leaveTypeName = requestToClear.leave_type_details?.name || 'Leave';
      showSuccessRef.current(`${leaveTypeName} request cleared successfully`);
      
      setShowClearModal(false);
      setRequestToClear(null);
    } catch (error) {
      console.error('Error clearing leave request:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.status?.[0] || 
                          'Failed to clear leave request';
      showErrorRef.current(errorMessage);
      setShowClearModal(false);
      setRequestToClear(null);
    }
  };

  const canEditRequest = (request) => {
    // Can edit if request is pending or approved AND hasn't started yet
    if (!['pending', 'approved'].includes(request.status)) {
      return false;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(request.start_date);
    startDate.setHours(0, 0, 0, 0);
    
    // Can only edit if start date is in the future
    return startDate > today;
  };

  const calculateBalancePercentage = () => {
    if (!leaveBalance) return 0;
    const used = parseFloat(leaveBalance.total_used);
    const total = parseFloat(leaveBalance.total_allocated);
    return total > 0 ? (used / total) * 100 : 0;
  };

  const calculatePendingPercentage = () => {
    if (!leaveBalance) return 0;
    const pending = parseFloat(leaveBalance.total_pending);
    const total = parseFloat(leaveBalance.total_allocated);
    return total > 0 ? (pending / total) * 100 : 0;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDays = (days) => {
    const num = parseFloat(days);
    return num % 1 === 0 ? Math.floor(num) : num.toFixed(1);
  };

  const isLeaveInProgress = (request) => {
    if (request.status !== 'approved') return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(request.start_date);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(request.end_date);
    endDate.setHours(0, 0, 0, 0);
    
    return startDate <= today && today <= endDate;
  };

  const calculateDaysRefunded = (request) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endDate = new Date(request.end_date);
    endDate.setHours(0, 0, 0, 0);
    
    // Calculate business days between today and original end date
    let daysRefunded = 0;
    let currentDate = new Date(today);
    currentDate.setDate(currentDate.getDate() + 1); // Start from tomorrow
    
    while (currentDate <= endDate) {
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        daysRefunded += 1;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return daysRefunded;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'cancelled': return 'status-cancelled';
      case 'completed': return 'status-completed';
      default: return 'status-pending';
    }
  };

  if (loading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="leave-management-content">
          <LoadingSpinner message="Loading leave information..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="leave-management-content">
        {error ? (
          <div className="error-state">
            <p className="error-message">{error}</p>
            <button onClick={fetchLeaveData} className="retry-button">
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Header Section with Compact Balance */}
            <div className="leave-header-compact">
              <div className="leave-header-left">
                <img src={logo} alt="IMT" className="leave-header-logo" />
                <div>
                  <h1 className="hms-screen-title">Leave Management</h1>
                  <p className="leave-subtitle">Manage your annual leave allocation and requests</p>
                </div>
              </div>
              <div className="leave-header-right">
                {leaveBalance && (
                  <div className="compact-balance-card">
                    {/* Unlimited Leave Usage Section - Left Side */}
                    {leaveBalance.unlimited_leave_used && Object.keys(leaveBalance.unlimited_leave_used).length > 0 && (
                      <div className="balance-section unlimited-section">
                        <div className="balance-section-header">
                          <span className="balance-section-title">Other Leave Usage</span>
                        </div>
                        <div className="unlimited-leave-list">
                          {Object.entries(leaveBalance.unlimited_leave_used).map(([code, days]) => {
                            const leaveTypeNames = {
                              'TL': 'Travel',
                              'HO': 'Home Office',
                              'SL': 'Sick Leave',
                              'ML': 'Maternity/Paternity'
                            };
                            return (
                              <div key={code} className="unlimited-leave-item">
                                <span className="unlimited-leave-name">{leaveTypeNames[code] || code}</span>
                                <span className="unlimited-leave-days">{formatDays(days)} days</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Main Balance Container - Right Side */}
                    <div className="main-balance-container">
                      {/* Avatar and Label Header Container */}
                      <div className="balance-header-container">
                        <div className="compact-balance-label-wrapper">
                          <div className="compact-balance-label">Leave Balance Overview</div>
                          {/* Year Toggle - Only show if there are multi-year leave requests */}
                          {hasMultiYearLeaves && (
                            <div className="leave-year-toggle">
                              <button
                                className={`year-toggle-btn ${selectedYear === new Date().getFullYear() ? 'active' : ''}`}
                                onClick={() => {
                                  const currentYear = new Date().getFullYear();
                                  setSelectedYear(currentYear);
                                  selectedYearRef.current = currentYear;
                                  fetchLeaveBalance(currentYear);
                                }}
                              >
                                {new Date().getFullYear()}
                              </button>
                              <button
                                className={`year-toggle-btn ${selectedYear === new Date().getFullYear() + 1 ? 'active' : ''}`}
                                onClick={() => {
                                  const nextYear = new Date().getFullYear() + 1;
                                  setSelectedYear(nextYear);
                                  selectedYearRef.current = nextYear;
                                  fetchLeaveBalance(nextYear);
                                }}
                              >
                                {new Date().getFullYear() + 1}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="leave-balance-avatar">
                          {currentUserAvatar ? (
                            <img 
                              src={currentUserAvatar} 
                              alt="Your profile" 
                              className="leave-avatar-image"
                            />
                          ) : (
                            <div className="leave-avatar-initials">
                              {user?.first_name?.[0] || ''}{user?.last_name?.[0] || ''}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Annual Leave Section */}
                      <div className="balance-section annual-section">
                        <div className="balance-section-header">
                          <span className="balance-section-title">Annual Leave</span>
                          <span className="balance-section-value">
                            {formatDays(leaveBalance.available_days)}/{formatDays(leaveBalance.total_allocated)}
                          </span>
                        </div>
                        <div className="compact-balance-progress-bar">
                          <div 
                            className="compact-balance-progress-fill"
                            style={{ width: `${(parseFloat(leaveBalance.total_used) / parseFloat(leaveBalance.total_allocated)) * 100}%` }}
                          ></div>
                          <div 
                            className="compact-balance-progress-pending"
                            style={{ 
                              left: `${(parseFloat(leaveBalance.total_used) / parseFloat(leaveBalance.total_allocated)) * 100}%`,
                              width: `${(parseFloat(leaveBalance.total_pending) / parseFloat(leaveBalance.total_allocated)) * 100}%` 
                            }}
                          ></div>
                          <div 
                            className="compact-balance-progress-available"
                            style={{ 
                              left: `${((parseFloat(leaveBalance.total_used) + parseFloat(leaveBalance.total_pending)) / parseFloat(leaveBalance.total_allocated)) * 100}%`,
                              width: `${(parseFloat(leaveBalance.available_days) / parseFloat(leaveBalance.total_allocated)) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <div className="balance-section-stats">
                          <span className="stat-item">Used: {formatDays(leaveBalance.total_used)}</span>
                          <span className="stat-item">Pending: {formatDays(leaveBalance.total_pending)}</span>
                        </div>
                      </div>

                      {/* Personal/Bereavement Leave Section */}
                      <div className="balance-section personal-bereavement-section">
                        <div className="balance-section-header">
                          <span className="balance-section-title">Personal/Bereavement</span>
                          <span className="balance-section-value">
                            {formatDays(leaveBalance.personal_bereavement_available)}/{formatDays(leaveBalance.personal_bereavement_allocated)}
                          </span>
                        </div>
                        <div className="compact-balance-progress-bar">
                          <div 
                            className="compact-balance-progress-fill"
                            style={{ width: `${(parseFloat(leaveBalance.personal_bereavement_used) / parseFloat(leaveBalance.personal_bereavement_allocated)) * 100}%` }}
                          ></div>
                          <div 
                            className="compact-balance-progress-pending"
                            style={{ 
                              left: `${(parseFloat(leaveBalance.personal_bereavement_used) / parseFloat(leaveBalance.personal_bereavement_allocated)) * 100}%`,
                              width: `${(parseFloat(leaveBalance.personal_bereavement_pending) / parseFloat(leaveBalance.personal_bereavement_allocated)) * 100}%` 
                            }}
                          ></div>
                          <div 
                            className="compact-balance-progress-available"
                            style={{ 
                              left: `${((parseFloat(leaveBalance.personal_bereavement_used) + parseFloat(leaveBalance.personal_bereavement_pending)) / parseFloat(leaveBalance.personal_bereavement_allocated)) * 100}%`,
                              width: `${(parseFloat(leaveBalance.personal_bereavement_available) / parseFloat(leaveBalance.personal_bereavement_allocated)) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <div className="balance-section-stats">
                          <span className="stat-item">Used: {formatDays(leaveBalance.personal_bereavement_used)}</span>
                          <span className="stat-item">Pending: {formatDays(leaveBalance.personal_bereavement_pending)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Interactive Calendar Widget - Always Visible */}
            <LeaveCalendarWidget
              leaveRequests={leaveRequests}
              onDateClick={(date) => {
                // Handle clicking on a date without leave - open request modal with prefilled date
                // Only allow this for "My Leave" view, not "Team Leave" view
                if (calendarView === 'my') {
                  setPrefilledStartDate(date);
                  setShowRequestModal(true);
                }
              }}
              onRequestClick={(requests, date) => {
                // Handle clicking on a date with leave requests - show day leave modal
                console.log('Leave requests clicked:', requests, 'on date:', date);
                setSelectedDayLeaveRequests(requests);
                setSelectedDayDate(date);
                setShowDayLeaveModal(true);
              }}
              onRequestLeave={handleRequestLeave}
              hasTeam={supervisorInfo.has_team}
              teamLeaveRequests={teamLeaveRequests}
              currentView={calendarView}
              onViewChange={handleCalendarViewChange}
              departments={supervisorInfo.departments || []}
              selectedDepartment={selectedDepartment}
              onDepartmentChange={handleDepartmentChange}
            />

            {/* Leave History */}
            <div className="leave-history">
              {(() => {
                // The table mirrors the calendar's toggle: "my" shows the
                // user's own requests (full lifecycle actions), "team" shows
                // the team's approved and past leave (read-only; full management
                // happens in the request modal).
                const isTeamTable = calendarView === 'team' && supervisorInfo.has_team;
                const tableRequests = isTeamTable ? teamLeaveRequests : leaveRequests;
                const tableTitle = isTeamTable ? 'Team Leave Requests' : 'Recent Leave Requests';
                return (
                  <>
                    <h3>{tableTitle}</h3>
                    {tableRequests.length > 0 ? (
                      <>
                        <div className="user-leave-table-wrapper">
                          <table className="user-leave-table">
                          <thead>
                            <tr>
                              {isTeamTable && <th>Employee</th>}
                              <th>Leave Type</th>
                              <th>Start Date</th>
                              <th>End Date</th>
                              <th>Days</th>
                              <th>Status</th>
                              <th>Reason</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableRequests.slice(0, leaveRequestsDisplayLimit).map(request => (
                              <tr
                                key={request.id}
                                onClick={() => handleViewRequest(request.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                {isTeamTable && (
                                  <td className="user-leave-employee-cell">
                                    {request.employee_name || '—'}
                                  </td>
                                )}
                                <td>
                                  <div className="user-leave-type-cell">
                                    <span
                                      className="user-leave-type-dot"
                                      style={{backgroundColor: request.leave_type_details?.color || '#4CAF50'}}
                                    ></span>
                                    <span className="user-leave-type-text">
                                      {request.leave_type_details?.name || 'Absent'}
                                    </span>
                                  </div>
                                </td>
                                <td>{formatDate(request.start_date)}</td>
                                <td>{formatDate(request.end_date)}</td>
                                <td className="user-leave-days-cell">{formatDays(request.total_days)}</td>
                                <td>
                                  <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                  </span>
                                </td>
                                <td className="user-leave-reason-cell">
                                  {request.reason ? (
                                    <span title={request.reason}>
                                      {request.reason.length > 50
                                        ? `${request.reason.substring(0, 50)}...`
                                        : request.reason
                                      }
                                    </span>
                                  ) : (
                                    <span className="user-leave-no-reason">—</span>
                                  )}
                                </td>
                                <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                                  <div className="action-dropdown">
                                    <button
                                      className="gear-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(activeDropdown === request.id ? null : request.id);
                                      }}
                                      aria-label="Actions"
                                    >
                                      Actions
                                    </button>
                                    {activeDropdown === request.id && (
                                      <div className="dropdown-menu" ref={dropdownRef}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewRequest(request.id);
                                            setActiveDropdown(null);
                                          }}
                                        >
                                          View Details
                                        </button>
                                        {/* Lifecycle actions (edit/cancel/return/clear) only apply
                                            to the user's own leave, not supervised team members'. */}
                                        {!isTeamTable && canEditRequest(request) && (
                                          <>
                                            <hr />
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditLeave(request);
                                                setActiveDropdown(null);
                                              }}
                                            >
                                              Edit Leave
                                            </button>
                                          </>
                                        )}
                                        {!isTeamTable && request.status === 'pending' && (
                                          <>
                                            <hr />
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowCancelModal(request);
                                                setActiveDropdown(null);
                                              }}
                                              className="danger"
                                            >
                                              Cancel Request
                                            </button>
                                          </>
                                        )}
                                        {!isTeamTable && request.status === 'approved' && !isLeaveInProgress(request) && (
                                          <>
                                            <hr />
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowCancelModal(request);
                                                setActiveDropdown(null);
                                              }}
                                              className="danger"
                                            >
                                              Cancel Request
                                            </button>
                                          </>
                                        )}
                                        {!isTeamTable && request.status === 'approved' && isLeaveInProgress(request) && (
                                          <>
                                            <hr />
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleReturnEarly(request);
                                                setActiveDropdown(null);
                                              }}
                                              className="danger"
                                            >
                                              Return Early
                                            </button>
                                          </>
                                        )}
                                        {!isTeamTable && request.status === 'cancelled' && (
                                          <>
                                            <hr />
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowClearModal(request);
                                              }}
                                              className="danger"
                                            >
                                              Clear
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                        {/* Load More Button */}
                        {tableRequests.length > leaveRequestsDisplayLimit && (
                          <div className="user-leave-load-more">
                            <button
                              className="btn-load-more"
                              onClick={() => setLeaveRequestsDisplayLimit(prev => prev + 10)}
                            >
                              Load More ({tableRequests.length - leaveRequestsDisplayLimit} remaining)
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="no-requests">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        <h4>{isTeamTable ? 'No Team Leave' : 'No Leave Requests'}</h4>
                        <p>
                          {isTeamTable
                            ? "None of your supervised team members currently have approved leave."
                            : "You haven't submitted any leave requests yet."}
                        </p>
                        {!isTeamTable && (
                          <button className="btn-primary" onClick={handleRequestLeave}>
                            Request Your First Leave
                          </button>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}

        {/* Leave Request Modal */}
      <LeaveRequestModal
        isOpen={showRequestModal}
        onClose={() => {
          setShowRequestModal(false);
          setPrefilledStartDate(null);
        }}
        onRequestSubmitted={handleRequestSubmitted}
        leaveBalance={leaveBalance}
        existingLeaveRequests={leaveRequests}
        prefilledStartDate={prefilledStartDate}
      />

      {/* Day Leave Details Modal - Shows all leave requests for a specific day */}
      {showDayLeaveModal && selectedDayDate && (
        <DayLeaveDetailsModal
          date={selectedDayDate}
          leaveRequests={selectedDayLeaveRequests}
          onClose={() => {
            setShowDayLeaveModal(false);
            setSelectedDayLeaveRequests([]);
            setSelectedDayDate(null);
          }}
          isTeamView={calendarView === 'team'}
        />
      )}

      {/* Leave Request View Modal - For viewing individual leave request details from table */}
      {showViewModal && selectedRequestId && (
        <LeaveRequestViewModal
          requestId={selectedRequestId}
          onClose={() => {
            setShowViewModal(false);
            setSelectedRequestId(null);
          }}
          isAdmin={false}
          // Route to the team endpoint when the row was clicked from the team
          // table; otherwise hit the user's own request endpoint.
          isTeamView={calendarView === 'team' && supervisorInfo.has_team}
        />
      )}

      {/* Edit Leave Request Modal */}
      {showEditModal && requestToEdit && (
        <EditLeaveRequestModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setRequestToEdit(null);
          }}
          onRequestUpdated={handleRequestUpdated}
          leaveRequest={requestToEdit}
          leaveBalance={leaveBalance}
          existingLeaveRequests={leaveRequests}
        />
      )}

        {/* Cancel Leave Confirmation Modal */}
        <CancelLeaveConfirmModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setRequestToCancel(null);
            setIsEarlyReturn(false);
            setDaysToRefund(0);
          }}
          onConfirm={handleCancelRequest}
          leaveRequest={requestToCancel}
          formatDate={formatDate}
          formatDays={formatDays}
          isEarlyReturn={isEarlyReturn}
          daysRefunded={daysToRefund}
        />

        {/* Clear Leave Confirmation Modal */}
        <ClearLeaveConfirmModal
          isOpen={showClearModal}
          onClose={() => {
            setShowClearModal(false);
            setRequestToClear(null);
          }}
          onConfirm={handleClearRequest}
          leaveRequest={requestToClear}
          formatDate={formatDate}
          formatDays={formatDays}
        />
        
        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />
        
        {/* Back to Top Button */}
        <BackToTopButton />
        
        {/* Footer */}
        <Footer />
      </div>
    </Layout>
  );
}

export default withAuth(UserLeaveManagement);
